import { IWedding_Website_Section } from '@bridebook/models/source/models/Weddings.types';
import { NON_REORDERABLE_SECTIONS } from '../constants';
import { EMPTY_WEBSITE_TEMPLATE } from '../constants/emptyWebsiteTemplate';
import { TSectionName } from '../types';

/**
 * Build sectionsOrderDict from current website sections.
 * Returns a dictionary with position as key and section name as value,
 * only including visible reorderable sections.
 * Example: {0: "ourStory", 1: "primaryVenue", 2: "faq"}
 */
export const buildSectionsOrderDict = (
  sections: IWedding_Website_Section[],
): Record<number, TSectionName> => {
  const reorderableSections = sections.filter(
    (section) =>
      !NON_REORDERABLE_SECTIONS.includes(section.name as TSectionName) && section.visible !== false,
  );

  return reorderableSections.reduce(
    (acc, section, index) => {
      acc[index] = section.name as TSectionName;
      return acc;
    },
    {} as Record<number, TSectionName>,
  );
};

/**
 * Check if sections are in the default order (ignoring hidden sections).
 * Compares visible reorderable sections against the default order in EMPTY_WEBSITE_TEMPLATE.
 */
export const isDefaultSectionsOrder = (sections: IWedding_Website_Section[]): boolean => {
  const visibleReorderableSections = sections
    .filter(
      (section) =>
        !NON_REORDERABLE_SECTIONS.includes(section.name as TSectionName) &&
        section.visible !== false,
    )
    .map((section) => section.name);

  const defaultReorderableSections = EMPTY_WEBSITE_TEMPLATE.sections
    .filter((section) => !NON_REORDERABLE_SECTIONS.includes(section.name as TSectionName))
    .map((section) => section.name);

  // Filter default sections to only include those that are visible in current sections
  const expectedOrder = defaultReorderableSections.filter((name) =>
    visibleReorderableSections.includes(name),
  );

  return (
    visibleReorderableSections.length === expectedOrder.length &&
    visibleReorderableSections.every((name, index) => name === expectedOrder[index])
  );
};
